import { useMemo } from "react";

import moment from "moment";
import { useQueryParam, withDefault } from "use-query-params";

import { useUser } from "@components/user-context";
import { deriveComparisonDateRange } from "@features/sales-v3/utils/compare-dates-helpers";
import {
  type CompareDateRange,
  CustomJsonParam,
  type DateInterval,
  type DateRangeChoiceV3,
  type FixedDateInterval,
  todayLocalAsISODate,
} from "@north-beam/nb-common";
import { parseDateRangeArgument } from "@utils/index";

const DateRangeQueryParam = CustomJsonParam<
  FixedDateInterval | DateRangeChoiceV3
>();

const CompareDateRangeQueryParam = CustomJsonParam<CompareDateRange>();

export type UseDateRangeReturnType = {
  dateRange: FixedDateInterval | DateRangeChoiceV3;
  dateRangeInterval: DateInterval;
  setDateRange: (val: FixedDateInterval | DateRangeChoiceV3) => void;
};

export const useDateRange = () => {
  const { user } = useUser();
  const [dateRange, setDateRange] = useQueryParam(
    "dateRange",
    withDefault(DateRangeQueryParam, "last7Days"),
    { updateType: "pushIn" },
  );
  const [compareDateRange, setCompareDateRange] = useQueryParam(
    "compareDateRange",
    withDefault<CompareDateRange, CompareDateRange>(
      CompareDateRangeQueryParam,
      { value: "previousPeriod" },
    ),
    { updateType: "pushIn" },
  );

  const dateRangeInterval = useMemo(
    () => parseDateRangeArgument(dateRange, todayLocalAsISODate()),
    [dateRange],
  );

  const compareDateRangeInterval = useMemo(() => {
    return deriveComparisonDateRange(
      dateRangeInterval,
      compareDateRange,
      user.timezone,
      "daily",
    );
  }, [dateRangeInterval, compareDateRange, user.timezone]);

  return {
    dateRange,
    dateRangeInterval: {
      start: moment(dateRangeInterval.startDate)
        .tz(user.timezone)
        .startOf("day")
        .toDate(),
      end: moment(dateRangeInterval.endDate)
        .tz(user.timezone)
        .endOf("day")
        .toDate(),
    },
    compareDateRange,
    compareDateRangeInterval: {
      start: moment(compareDateRangeInterval?.startDate)
        .tz(user.timezone)
        .startOf("day")
        .toDate(),
      end: moment(compareDateRangeInterval?.endDate)
        .tz(user.timezone)
        .endOf("day")
        .toDate(),
    },
    setCompareDateRange,
    setDateRange,
  };
};
